import { Accordion } from 'react-bootstrap';
import type { AccordionProps } from 'react-bootstrap';
import { Ai2Layout } from '../../layout/Ai2Layout';
import './styles/settings-page.css';
import './styles/preference-page.css';
import { useUpdateUserDashboardColumns } from '../../hooks/preferences/useUpdateUserDashboardColumns';
import { useUpdateTagsPreferences } from '../../hooks/preferences/useUpdateTagsPreferences';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDashboardColumns } from '../../hooks';
import { useForm } from '../../hooks/useForm';
import { useGlobalLoading } from '../../../context';
import TagsSetting from './components/TagsSetting';
import ListInformationSetting from './components/ListInformationSetting';
import PathologySetting from './components/PathologySetting';
import usePreferences from './hook/usePreferences';
import TasksSetting from './components/TasksSetting';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { useTeamTaskTemplate } from '../../components/Users/ManageTask/useTeamTaskTemplate';

export enum FormCategory {
  LIST_INFORMATION = 'listInfo',
  TAGS = 'tags',
  PATHOLOGY = 'pathologies',
  TASKS = 'tasks',
}

type FormState = Record<FormCategory, Record<string, boolean>>;

interface FormChangeEvent {
  target: { name: string; value: boolean; category: FormCategory };
}

export interface DashboardColumn {
  id: string;
  enabled: boolean;
}

export enum PreferencesTabs {
  LIST_INFORMATION = 'list-information-settings',
  PATHOLOGY = 'pathology-settings',
  TAGS = 'tags-settings',
  TASKS = 'tasks-settings',
}

export type PreferencesTabsType =
  (typeof PreferencesTabs)[keyof typeof PreferencesTabs];

export const Preferences = () => {
  const [activeTab, setActiveTab] = useState<PreferencesTabsType>(
    PreferencesTabs.LIST_INFORMATION,
  );
  const { updatePathologyPreferences, initialized, updateTaskPreferences } =
    usePreferences();
  const { allPathologies, hiddenPathologies, hiddenTasks } = useSelector(
    (state: RootState) => state.preferences,
  );

  const { clinics } = useSelector((state: RootState) => state.clinicianProfile);
  const { columnData } = useTeamTaskTemplate(clinics);
  const { updateTagsPreferences, hiddenTags, allTags } =
    useUpdateTagsPreferences();
  const { loadingPreferences } = useGlobalLoading();
  const { dashboardColumns } = useDashboardColumns();

  const formData = useMemo<FormState>(
    () => ({
      [FormCategory.TASKS]: columnData.reduce(
        (acc, curr) => {
          acc[curr.id] = !hiddenTasks?.includes(curr.task);
          return acc;
        },
        {} as Record<string, boolean>,
      ),
      [FormCategory.LIST_INFORMATION]: dashboardColumns.reduce(
        (acc: Record<string, boolean>, curr: DashboardColumn) => {
          acc[curr.id] = curr.enabled;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
      [FormCategory.TAGS]: allTags.reduce(
        (acc, curr) => {
          acc[`tag-${curr.id}`] = !hiddenTags.includes(curr.shortName);
          return acc;
        },
        {} as Record<string, boolean>,
      ),
      [FormCategory.PATHOLOGY]: (allPathologies || []).reduce(
        (acc, curr) => {
          acc[curr.id] = !hiddenPathologies?.includes(curr.name);
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    }),
    [dashboardColumns, allTags, allPathologies, hiddenTags, hiddenPathologies],
  );

  const { formState, onInputChange, isDirty, onResetForm, setIsDirty } =
    useForm<FormState>(formData);

  const { postUserDashboardColumns, isLoading: isUpdateUserDashboardColumns } =
    useUpdateUserDashboardColumns();

  // Initialize form only when data is first loaded
  useEffect(() => {
    if (initialized) {
      onResetForm(formData);
    }
  }, [initialized]); // Intentionally omit formData and onResetForm

  const getSelectedTagNames = () => {
    if (!formState[FormCategory.TAGS]) {
      return allTags.map((tag) => tag.shortName);
    }
    const tagEntries = Object.entries(formState[FormCategory.TAGS]);

    return tagEntries
      .filter(([_, value]) => value === false)
      .map(([key]) => allTags.find((tag) => `tag-${tag.id}` === key)?.shortName)
      .filter(Boolean) as string[];
  };

  const getSelectedPathologies = () => {
    if (!allPathologies) {
      return undefined;
    }
    if (!formState[FormCategory.PATHOLOGY]) {
      return allPathologies.map((pathology) => pathology.name);
    }

    const entries = Object.entries(formState[FormCategory.PATHOLOGY]);
    const selected = entries
      .filter(([_, value]) => value === false)
      .map(([key]) => key);
    const selectedPathologies = allPathologies.filter((pathology) =>
      selected.includes(pathology.id),
    );
    return selectedPathologies.map((pathology) => pathology.name);
  };

  const getSelectedTasks = () => {
    if (!columnData) {
      return undefined;
    }

    const entries = Object.entries(formState[FormCategory.TASKS]);
    const selected = entries
      .filter(([_, value]) => value === false)
      .map(([key]) => key);
    const selectedTasks = columnData.filter((task) =>
      selected.includes(task.id),
    );
    return selectedTasks.map((task) => task.task);
  };

  const getDashboardColumns = () => {
    if (!formState[FormCategory.LIST_INFORMATION]) {
      return dashboardColumns;
    }
    const columnEntries = Object.entries(
      formState[FormCategory.LIST_INFORMATION],
    );

    return columnEntries.map(([key, value]) => ({ id: key, enabled: value }));
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const selectedTagNames = getSelectedTagNames();
      const selectedPathologies = getSelectedPathologies();
      const selectedTasks = getSelectedTasks();
      // Get string array of hidden tags
      try {
        await Promise.all([
          updateTaskPreferences(selectedTasks),
          updateTagsPreferences({ selectedTags: selectedTagNames }),
          updatePathologyPreferences(selectedPathologies),
          postUserDashboardColumns(getDashboardColumns()),
        ]);
        setIsDirty(false);
      } catch (error) {
        onResetForm(formData);
        console.error('Error saving preferences:', error);
      }
    },
    [formState, postUserDashboardColumns, updateTagsPreferences, setIsDirty],
  );

  const handleAccordionSelect = useCallback<
    NonNullable<AccordionProps['onSelect']>
  >((eventKey) => {
    if (eventKey && typeof eventKey === 'string') {
      setActiveTab(eventKey as PreferencesTabsType);
    }
  }, []);

  const handleInputChange = useCallback(
    (e: FormChangeEvent) => {
      const { name, value, category } = e.target;
      onInputChange({
        target: {
          name: category,
          value: { ...formState[category], [name]: value },
        },
      });
    },
    [onInputChange, formState],
  );

  const handleResetForm = useCallback(() => {
    onResetForm(formData);
  }, [formData, onResetForm]);

  const onChangeValue = (
    name: string,
    value: boolean,
    category: FormCategory,
  ) => handleInputChange({ target: { name, value, category } });

  return (
    <Ai2Layout>
      <div className="settings-page">
        <div className="settings-page-title">Preferences</div>
        <div className="preference-container">
          <Accordion activeKey={activeTab} onSelect={handleAccordionSelect}>
            <ListInformationSetting
              activeTab={activeTab}
              formState={formState.listInfo}
              onChangeInformation={(name, value) =>
                onChangeValue(name, value, FormCategory.LIST_INFORMATION)
              }
            />
            <TagsSetting
              allTags={allTags}
              onChangeTag={(name, value) =>
                onChangeValue(name, value, FormCategory.TAGS)
              }
              tagState={formState.tags}
              activeTab={activeTab}
            />
            <PathologySetting
              pathologyState={formState.pathologies}
              activeTab={activeTab}
              onChangePathology={(name, value) =>
                onChangeValue(name, value, FormCategory.PATHOLOGY)
              }
            />
            <TasksSetting
              activeTab={activeTab}
              tasks={columnData}
              taskState={formState.tasks}
              onChangeTask={(name, value) =>
                onChangeValue(name, value, FormCategory.TASKS)
              }
            />
          </Accordion>
        </div>
        {isDirty ? (
          <div className="settings-page-actions">
            <button
              type="button"
              className="cancel-change"
              onClick={handleResetForm}
              disabled={isUpdateUserDashboardColumns || loadingPreferences}
            >
              Cancel changes
            </button>
            <button
              type="button"
              className="save-change"
              onClick={handleSubmit}
              disabled={isUpdateUserDashboardColumns || loadingPreferences}
            >
              Save changes
            </button>
          </div>
        ) : null}
      </div>
    </Ai2Layout>
  );
};
